import * as PouchDBCore from 'pouchdb-core';
import * as PouchDBAdapterIDB from 'pouchdb-adapter-idb';
import * as PouchDBAdapterWebSQL from 'pouchdb-adapter-websql';
import * as PouchDBMapReduce from 'pouchdb-mapreduce';
import { logger } from '../Logger';
import { core } from '../Core';
import { Common } from '../Common';

const PouchDBBinaryUtils = require('pouchdb-binary-utils');
const SAFARI_EMPTY_BLOB_PROBE_PATCHED = '__iccSafariEmptyBlobProbePatched';
const SAFARI_EMPTY_BLOB_PROBE_ERROR = 'ICC_SAFARI_EMPTY_BLOB_PROBE_DISABLED';

export type PouchDBConfiguration =
    | PouchDB.Configuration.DatabaseConfiguration
    | PouchDB.AdapterWebSql.Configuration
    | PouchDB.IdbAdapter.IdbAdapterConfiguration;

declare var self: any;

window.addEventListener('error', function(event: ErrorEvent) {
    if (event && event.error && event.error.name === 'NotFoundError') {
        clearIDB(true);
    }
});

function PouchDBICC(adapter): any {
    let name, func, addToPreferredAdapters;
    const x = {
        adapter(adapterName, adapterFunction, adapterAddToPreferredAdapters) {
            name = adapterName;
            func = adapterFunction;
            addToPreferredAdapters = adapterAddToPreferredAdapters;
        },
    };
    adapter(x);
    return function(PDB) {
        func.valid = () => typeof indexedDB !== 'undefined' && typeof IDBKeyRange !== 'undefined';
        PDB.adapter(name, func, addToPreferredAdapters);
    };
}

disableSafariPouchDbEmptyBlobProbe();

export const PouchDB = PouchDBCore.plugin(PouchDBICC(PouchDBAdapterIDB))
    .plugin(PouchDBAdapterWebSQL)
    .plugin(PouchDBMapReduce);

/**
 * Przeciążenie kontruktora PouchDB, zapamiętanie nazw baz
 * @param {string} name Nazwa bazy
 */
export function PouchDBNew<T>(name: string, user: any, config?: PouchDBConfiguration, localDbVersion?: number) {
    if (!user) {
        throw new Error('Brak użytkownika! ' + name);
    }

    if (!core.isWorker() && !localDbVersion) {
        localDbVersion = Number(localStorage.getItem(`localDbVersion`) || '0');
        localStorage.setItem(`localDbVersion`, String(localDbVersion));
    }

    const dbName = `${user.marketId}_${user.id || ''}_${name}_${user.user_language}_${localDbVersion}`;
    if (core.isWorker()) {
        self.postMessage({ subject: 'iDB', name: dbName });
    } else {
        const iDBs = JSON.parse(window.localStorage.getItem('iDBs') || '[]');
        if (iDBs.indexOf(dbName) < 0) {
            iDBs.push(dbName);
            window.localStorage.setItem('iDBs', JSON.stringify(iDBs));
        }
    }
    config = Common.extend({}, config, {
        adapter: 'idb',
    });
    logger.debug('Tworzenie bazy danych', dbName, config);
    return new PouchDB<T>(dbName, config);
}

/**
 * Czyszczenie wszystkich baz
 */
export async function clearIDB(redirect = false) {
    const dbVersion = localStorage.getItem(`localDbVersion`) || '0';
    localStorage.setItem(`localDbVersion`, String(Number(dbVersion) + 1));
    const iDBs = JSON.parse(window.localStorage.getItem('iDBs') || '[]');
    for (let i = 0; i < iDBs.length; i++) {
        window.indexedDB.deleteDatabase('_pouch_' + iDBs[i]);
    }
    window.localStorage.removeItem(iDBs);

    for (const k in window.localStorage) {
        if (k.substring(0, 7) === '_pouch_' || k.substring(0, 6) === 'state_') {
            window.indexedDB.deleteDatabase(k);
            window.localStorage.removeItem(k);
        }
    }
    if(window.location.pathname.includes('/app/redirect')) {
        const redirectUrl = window.location.pathname.split('/')
        sessionStorage.setItem('redirect', atob(redirectUrl[redirectUrl.length - 1]))
    }

    if (redirect) {
        logger.error(new Error('Przekierowanie na app po naprawie bazy.'));
        window.location.pathname = '/app/sync';
    }
}

function disableSafariPouchDbEmptyBlobProbe() {
    if (!isSafariBrowser()) {
        return;
    }

    const binaryUtils = PouchDBBinaryUtils as any;
    if (binaryUtils[SAFARI_EMPTY_BLOB_PROBE_PATCHED] || typeof binaryUtils.blob !== 'function') {
        return;
    }

    const originalBlob = binaryUtils.blob;
    try {
        binaryUtils.blob = function(parts?: any[], properties?: any) {
            if (isPouchDbEmptyBlobProbe(parts, properties)) {
                throw new Error(SAFARI_EMPTY_BLOB_PROBE_ERROR);
            }
            return originalBlob.apply(this, arguments);
        };
        binaryUtils[SAFARI_EMPTY_BLOB_PROBE_PATCHED] = true;
    } catch (error) {
        void error;
    }
}

function isPouchDbEmptyBlobProbe(parts?: any[], properties?: any) {
    return Array.isArray(parts)
        && parts.length === 1
        && parts[0] === ''
        && (!properties || Object.keys(properties).length === 0);
}

function isSafariBrowser() {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    return /safari/i.test(userAgent)
        && /applewebkit/i.test(userAgent)
        && !/(chrome|chromium|crios|fxios|edg|opios|opr|android)/i.test(userAgent);
}
